"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { Container } from "@/components/Container";
import { MaskReveal, Counter } from "@/components/Motion";
import { resolveStats, resolveStatsNote, HOME_STATS, HOME_STATS_NOTE } from "@/lib/stats";

// The band's figures and its as-of note now live in lib/stats (HOME_STATS /
// HOME_STATS_NOTE) so the blog article template can render the same numbers.
// /admin/pages ("home") still overrides each field.

// Headline line with the first word teal-accented (matches the default
// "Enterprise Managed" treatment when a CMS override is present).
function AccentFirstWord({ text }: { text: string }) {
  const [first, ...rest] = text.split(" ");
  return (
    <>
      <span className="text-brand-accent-light">{first}</span>
      {rest.length > 0 ? " " + rest.join(" ") : ""}
    </>
  );
}

// The opening statement — full-bleed video behind a solid Outfit headline.
// "Enterprise" carries the teal accent (the only colour in the headline);
// everything else is solid white, no outlines. A stat band closes the frame.
export function Hero({ content = {} }: { content?: Record<string, string> } = {}) {
  // Admin-managed background media (Pages → Home). Empty keys keep the repo files.
  const videoSrc = content["hero.video"]?.trim() || "/videos/hero.mp4";
  const posterSrc = content["hero.poster"]?.trim() || "/images/hero-building.jpg";
  const stats = resolveStats(HOME_STATS, content);
  // In the admin preview the stat figures are click-to-edit, and the editor
  // saves whatever text the element holds. The animated counter starts at zero
  // (and rAF is throttled in the preview iframe), so it would offer — and on
  // blur save — "0" over the real figure. Render the settled number instead.
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    setEditing(new URLSearchParams(window.location.search).has("tsedit"));
  }, []);
  const statText = (s: { value: number; decimals: number; suffix: string }) =>
    `${s.decimals > 0 ? s.value.toFixed(s.decimals) : s.value.toLocaleString()}${s.suffix}`;
  const statsNote = resolveStatsNote(HOME_STATS_NOTE, content);
  const line1 = content["hero.line1"]?.trim();
  const line2 = content["hero.line2"]?.trim();
  const line3 = content["hero.line3"]?.trim();
  const hasCustomHeadline = !!(line1 || line2 || line3);
  // The hero clip is background decoration, and it used to be fetched at once
  // because autoplay makes the browser treat it as needed immediately — two
  // megabytes competing with the text and the poster for the first paint.
  // Holding the src back until the browser is idle lets the poster be the
  // first thing painted, which is the thing the LCP measurement is actually
  // looking at.
  //
  // Reduced motion never loads it at all: a looping background is exactly
  // what that setting is asking not to see, and the poster shows the same
  // scene standing still.
  const [clipSrc, setClipSrc] = useState<string | null>(null);
  const clipRef = useRef<HTMLVideoElement>(null);
  // autoplay is meant to fire when a src appears, but it is not something to
  // rely on for a src set after mount — ask for playback explicitly, and
  // ignore a refusal, which is what a browser does when it decides a
  // background clip has not earned the right to move.
  useEffect(() => {
    if (!clipSrc) return;
    clipRef.current?.play().catch(() => {});
  }, [clipSrc]);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const start = () => setClipSrc(videoSrc);
    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    if (w.requestIdleCallback) {
      const id = w.requestIdleCallback(start, { timeout: 2500 });
      return () => w.cancelIdleCallback?.(id);
    }
    const t = setTimeout(start, 1200);
    return () => clearTimeout(t);
  }, [videoSrc]);

  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.14]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative flex min-h-[min(100svh,62.5rem)] flex-col overflow-hidden bg-brand-dark text-white"
    >
      {/* L0 — cinematic video with slow scroll dolly (image swap deferred —
          marketing will supply final stills later) */}
      <motion.div style={{ y, scale }} className="absolute inset-0 h-full w-full">
        <video
          key={videoSrc}
          ref={clipRef}
          className="h-full w-full object-cover [filter:contrast(1.03)_brightness(0.72)_saturate(1.04)]"
          autoPlay
          muted
          loop
          playsInline
          poster={posterSrc}
          // No src until the page has settled — see the effect above. preload
          // stays "none" so nothing is fetched before that point.
          preload="none"
          src={clipSrc ?? undefined}
        />
      </motion.div>

      {/* L1 — scrims for legibility (darkened so the title and copy stay crisp) */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#303030]/90 via-[#303030]/50 to-[#303030]/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#303030]/70 to-transparent" />
      {/* L1b — uniform dark veil so the text stays pronounced over the video */}
      <div className="absolute inset-0 bg-brand-dark/40" />

      {/* Content — left-anchored headline, stat band along the bottom */}
      <motion.div style={{ opacity: fade }} className="relative flex flex-1 flex-col">
        <Container className="flex flex-1 flex-col justify-center pb-10 pt-32">
          <h1 data-cms-lines="hero.line1,hero.line2,hero.line3" className="max-w-4xl t-hero">
            <MaskReveal
              delay={0.1}
              lines={
                hasCustomHeadline
                  ? ([
                      line1 ? <AccentFirstWord key="l1" text={line1} /> : null,
                      line2 || null,
                      line3 || null,
                    ].filter(Boolean) as React.ReactNode[])
                  : [
                      <>
                        <span className="text-brand-accent-light">Enterprise</span> Managed
                      </>,
                      "Office Space",
                      "Solutions in India",
                    ]
              }
            />
          </h1>

          <p data-cms-key="hero.intro1" className="mt-7 max-w-2xl text-base font-normal leading-relaxed text-white/80">
            {content["hero.intro1"]?.trim() ||
              "Table Space is one of India's foremost managed office providers, delivering premium office solutions that support enterprises across every stage of their workspace journey."}
          </p>
          <p data-cms-key="hero.intro2" className="mt-4 max-w-2xl text-base font-normal leading-relaxed text-white/80">
            {content["hero.intro2"]?.trim() ||
              "From Custom-Built Offices to Ready-To-Move-in Suites, Table Space delivers fully managed workspaces for enterprises ensuring an end-to-end workspace experience. Our solutions are designed to inspire productivity and growth."}
          </p>
        </Container>

        {/* stat band — bottom edge */}
        <div className="border-t border-white/15 backdrop-blur-[2px]">
          <Container>
            <dl className="grid grid-cols-2 sm:grid-cols-4">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  className={`flex flex-col gap-1 py-5 md:py-6 ${
                    i % 2 !== 0 ? "border-l border-white/15 pl-6" : ""
                  } ${i !== 0 ? "sm:border-l sm:border-white/15 sm:pl-6" : ""}`}
                >
                  <dt
                    data-cms-key={`stat${i + 1}.value`}
                    className="t-title font-medium tabular-nums"
                  >
                    {editing ? (
                      statText(s)
                    ) : (
                      <Counter value={s.value} suffix={s.suffix} decimals={s.decimals} immediate />
                    )}
                  </dt>
                  <dd data-cms-key={`stat${i + 1}.label`} className="mono-meta uppercase text-white/55">{s.label}</dd>
                </div>
              ))}
            </dl>
            {statsNote && (
              <p
                data-cms-key="stats.note"
                className="mono-meta pb-5 text-white/45 md:pb-6"
              >
                {statsNote}
              </p>
            )}
          </Container>
        </div>
      </motion.div>
    </section>
  );
}
